import {
  ActionButton,
  Dialog,
  PictureEditor,
  PictureEditorRef,
  Stack,
  Text,
  useDialogController,
  useViewport,
} from '@amx/ux-twoui';
import { useTwoUIOperationDispatcher } from '@amx/ux-twoui-telemetry';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import { useCallback, useEffect, useRef, useState } from 'react';
import React from 'react';
import { useUpdatePhoto } from '../../hooks/useUpdateProfilePhoto';
import { RemovePhotoDialog } from './RemovePhotoDialog';
import i18next from 'i18next';
import { ArrowKeyMovementStrings } from '@amx/ux-twoui/dist/components/PictureEditor/PictureEditor';
import { MOBILE_BREAK_POINT, PROFILE_PHOTO_ALLOWED_OPERATIONS } from '../../shared/constants';
import { useTelemetry } from '@iamexperiences/ecos-telemetry';

interface IEditPhotoDialogProps {
  displayName: string;
  allowedOperations?: string;
  photoUrl?: string;
}

const getPhotoPickerStrings = (t: i18next.TranslationFunction) => {
  return {
    apply: t('apply'),
    cancel: t('cancel'),
    close: t('close'),
    dropZone: t('dropZone'),
    dropZoneEmptyAriaLabel: t('dropZoneEmptyAriaLabel'),
    dropZoneImageAriaLabel: t('dropZoneImageAriaLabel'),
    infoBarDismissButtonAriaLabel: t('infoBarDismissButtonAriaLabel'),
    imageTooSmall: t('imageTooSmall'),
    imageTooSmallError: t('imageTooSmallError'),
    inProgress: t('inProgress'),
    notAnImage: t('notAnImage'),
    photoSelected: () => t('photoSelected'),
    remove: t('remove'),
    somethingWentWrong: t('somethingWentWrong'),
    upload: t('upload'),
    uploadHeader: t('uploadHeader'),
    unsupportedBrowser: t('unsupportedBrowser'),
    zoom: t('zoom'),
    zoomIn: t('zoomIn'),
    zoomOut: t('zoomOut'),
    zoomAtMax: t('zoomAtMax'),
    imageScaleInformationString: ({ p0 }: { p0: string }) => t('imageScaleInformationString', { p0 }),
    success: t('success'),
    closeIconTitle: t('closeIconTitle'),
    closeIconAriaLabel: t('closeIconAriaLabel'),
    rotate: t('rotate'),
    clockwise: t('rotateRight'),
    counterClockwise: t('rotateLeft'),
    imagePositionInformationString: ({ p0 }: { p0: string }) => t('imagePositionInformationString', { p0 }),
    leftArrowKeyAriaLabel: () => t('leftArrowKeyAriaLabel'),
    rightArrowKeyAriaLabel: () => t('rightArrowKeyAriaLabel'),
    upArrowKeyAriaLabel: () => t('upArrowKeyAriaLabel'),
    downArrowKeyAriaLabel: () => t('downArrowKeyAriaLabel'),
  };
};

const operationName = 'updating-profile-photo';

export const EditPhotoDialog: React.FC<IEditPhotoDialogProps> = ({ displayName, photoUrl, allowedOperations }) => {
  const updatePhotoMutation = useUpdatePhoto();

  const dialogController = useDialogController();
  const twoUIDispatcher = useTwoUIOperationDispatcher();
  const [t] = useLocalization('Account');
  const telemetry = useTelemetry();

  const pictureEditorRef = useRef<PictureEditorRef | null>(null);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [isCanvasDirty, setIsCanvasDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const viewportType = useViewport();

  useEffect(() => {
    twoUIDispatcher({ name: operationName, status: 'finished' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (updatePhotoMutation.isSuccess) {
      dialogController.closeDialog();
      telemetry.reportCustomEvent('update-photo/change', { message: 'successfully' });
      twoUIDispatcher({ name: operationName, status: 'finished' });
    }
    if (updatePhotoMutation.isLoading) {
      twoUIDispatcher({ name: operationName, status: 'updating' });
    }
    if (updatePhotoMutation.isError) {
      setError(t('editPhotoGenericError'));
      telemetry.error('update-photo/change', updatePhotoMutation.error);
      twoUIDispatcher({ name: operationName, status: 'failed' });
    }
  }, [
    dialogController,
    t,
    telemetry,
    twoUIDispatcher,
    updatePhotoMutation.error,
    updatePhotoMutation.isError,
    updatePhotoMutation.isLoading,
    updatePhotoMutation.isSuccess,
  ]);

  const onCanvasUpdated = (newCanvas: HTMLCanvasElement, isCanvasDirty?: boolean) => {
    setCanvas(newCanvas);
    setIsCanvasDirty(isCanvasDirty || false);
  };

  const onSave = useCallback(async () => {
    if (!canvas) {
      return;
    }

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg');
    });

    if (!blob) {
      telemetry.error('update-photo/change', 'empty blob');
      return;
    }
    updatePhotoMutation.mutate(blob);
  }, [canvas, telemetry, updatePhotoMutation]);

  const onRemoveButtonClicked = useCallback(() => {
    dialogController.switchToDialog(<RemovePhotoDialog displayName={displayName} imageUrl={photoUrl!} />);
  }, [dialogController, displayName, photoUrl]);

  const onUploadButtonClicked = useCallback(() => {
    pictureEditorRef.current?.startUploadNewPhoto();
  }, []);

  const onCancelClick = useCallback(() => {
    dialogController.closeDialog();
  }, [dialogController]);

  const arrowKeyMovementStrings: ArrowKeyMovementStrings = {
    leftArrowKeyAriaLabel: t('leftArrowKeyAriaLabel'),
    rightArrowKeyAriaLabel: t('rightArrowKeyAriaLabel'),
    upArrowKeyAriaLabel: t('upArrowKeyAriaLabel'),
    downArrowKeyAriaLabel: t('downArrowKeyAriaLabel'),
  };
  return (
    <Dialog
      isOpen={true}
      closeButton={{
        'aria-label': t('close'),
        'data-bi-id': '',
      }}
      onDismiss={onCancelClick}
      dialogFooterProps={{
        buttonPrimary: {
          'aria-label': t('save'),
          text: t('save'),
          'data-bi-id': '',
          onClick: onSave,
          disabled: !isCanvasDirty,
          operationData: {
            progressViewOperationData: {
              componentName: 'edit-photo-dialog',
              operationName,
              defaultState: 'finished',
            },
          },
        },
        buttonSecondary: {
          'aria-label': t('cancel'),
          text: t('cancel'),
          'data-bi-id': '',
          onClick: onCancelClick,
        },
      }}
      staticBodyWidth={viewportType === MOBILE_BREAK_POINT ? undefined : '480px'}
      stickyHeaderFooter={{
        hasStickyHeaderFooters: true,
      }}
      title={t('changePhoto')}
    >
      <Stack padding="30px 0 0 0">
        <Stack horizontalAlign="center">
          <PictureEditor
            ref={pictureEditorRef}
            personDisplayName={displayName}
            personImageUrl={photoUrl || ''}
            onCanvasUpdated={onCanvasUpdated}
            operationData={{
              operationName: 'persona.editPhoto.editor.loadPhoto',
              componentName: 'persona.editPhoto.editor',
              defaultState: 'finished',
            }}
            photoSelectedString={'{0} selected'}
            arrowKeyMovementStrings={arrowKeyMovementStrings}
            getPhotoPickerStrings={getPhotoPickerStrings(t)}
          />
        </Stack>
        {error && (
          <div style={{ padding: '10px 0' }}>
            <Text colorOverride="error">{error}</Text>
          </div>
        )}
        {(photoUrl || isCanvasDirty) && (
          <div style={{ padding: '10px 0' }}>
            <Text>{t('changPhotoDescription')}</Text>
          </div>
        )}
        <ActionButton
          disabled={!allowedOperations?.includes(PROFILE_PHOTO_ALLOWED_OPERATIONS.update)}
          text={photoUrl || isCanvasDirty ? t('changePhoto') : t('addAPhoto')}
          aria-label={t('changePhoto')}
          data-bi-id={''}
          iconName="CloudUpload"
          onClick={onUploadButtonClicked}
        />
        <ActionButton
          disabled={!photoUrl || !allowedOperations?.includes(PROFILE_PHOTO_ALLOWED_OPERATIONS.delete)}
          text={t('removePhoto')}
          aria-label={t('removePhoto')}
          data-bi-id={''}
          iconName="Delete"
          onClick={onRemoveButtonClicked}
        />
      </Stack>
    </Dialog>
  );
};
